import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { getLocalTimeZone, today } from '@internationalized/date';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DatePickerInput } from '@/components/ui/date-picker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Clipboard, ClipboardCheck, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useProviderCheckoutDomainSettings } from '@/hooks/provider/useProviderCheckoutDomainSettings';
import {
  useGenerateCustomerCheckoutLink,
  useProviderCustomerCheckoutLinks,
  useProviderCustomerCheckoutLinkOptions,
} from '@/hooks/provider/useCustomerCheckoutLinks';
import type { ProviderCheckoutLinkCreateInput, ProviderCheckoutLinkCreateResult } from '@/types/provider-checkout-links';
import { resolveCheckoutOrigin } from '@/lib/checkout-origin';

interface CustomerCheckoutLinksDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  customer: {
    id: string;
    name: string;
    email: string;
  };
}

type CheckoutLinkFormValues = {
  productId: string;
  selectionType: 'plan' | 'package';
  planId: string;
  packageId: string;
  priceId: string;
  expiresAt?: string;
};

type LatestCheckoutLink = ProviderCheckoutLinkCreateResult & { productId: string };

const statusBadgeStyles: Record<string, { variant: 'default' | 'secondary' | 'outline'; className?: string }> = {
  active: { variant: 'default', className: 'bg-green-100 text-green-800' },
  expired: { variant: 'secondary', className: 'bg-amber-100 text-amber-800' },
  consumed: { variant: 'outline', className: 'text-muted-foreground' },
};

const pad2 = (value: number) => String(value).padStart(2, '0');

const formatDateTime = (value?: string | null) => {
  if (!value) return '--';
  const trimmed = value.trim();
  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${day}/${month}/${year}`;
  }
  try {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed);
    if (hasTimezone) {
      return `${pad2(parsed.getUTCDate())}/${pad2(parsed.getUTCMonth() + 1)}/${parsed.getUTCFullYear()}`;
    }
    return `${pad2(parsed.getDate())}/${pad2(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
  } catch (error) {
    console.error('Failed to format date', error);
    return value;
  }
};

const formatDateInputValue = (value: Date) =>
  `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;

const parseDateInput = (value?: string) => {
  if (!value) return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const parsed = new Date(year, month - 1, day, 23, 59, 59, 999);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null;
  }
  return parsed;
};

const getDefaultExpiryInputValue = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return formatDateInputValue(date);
};

export function CustomerCheckoutLinksDialog({ open, onOpenChange, customer }: CustomerCheckoutLinksDialogProps) {
  const { t } = useTranslation();
  const [latestLink, setLatestLink] = useState<LatestCheckoutLink | null>(null);
  const [copied, setCopied] = useState(false);
  const customerId = open ? customer.id : undefined;
  const minExpiryDate = useMemo(() => today(getLocalTimeZone()), []);
  const { data: checkoutLinksData, isLoading, isFetching, error, refetch } = useProviderCustomerCheckoutLinks(customerId);
  const generateLink = useGenerateCustomerCheckoutLink();
  const { data: checkoutDomainSettings } = useProviderCheckoutDomainSettings();

  const checkoutOrigin = useMemo(
    () =>
      resolveCheckoutOrigin(
        checkoutDomainSettings?.checkoutDomain,
        checkoutDomainSettings?.checkoutDomainEnabled ?? false,
      ),
    [checkoutDomainSettings?.checkoutDomain, checkoutDomainSettings?.checkoutDomainEnabled],
  );

  const schema = useMemo(
    () =>
      z
        .object({
          productId: z.string().trim().min(1, t('customers.checkoutLinks.validation.productRequired')),
          selectionType: z.enum(['plan', 'package']),
          planId: z.string().trim().optional(),
          packageId: z.string().trim().optional(),
          priceId: z.string().trim().min(1, t('customers.checkoutLinks.validation.priceRequired')),
          expiresAt: z
            .string()
            .trim()
            .optional()
            .refine((value) => !value || Boolean(parseDateInput(value)), {
              message: t('customers.checkoutLinks.validation.expiresAtInvalid'),
            })
            .refine(
              (value) => {
                if (!value) return true;
                const parsed = parseDateInput(value);
                return parsed ? parsed.getTime() > Date.now() : false;
              },
              {
                message: t('customers.checkoutLinks.validation.expiresAtFuture'),
              },
            ),
        })
        .superRefine((values, context) => {
          if (values.selectionType === 'plan' && !values.planId?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('customers.checkoutLinks.validation.planRequired'),
              path: ['planId'],
            });
          }
          if (values.selectionType === 'package' && !values.packageId?.trim()) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('customers.checkoutLinks.validation.packageRequired'),
              path: ['packageId'],
            });
          }
        }),
    [t],
  );

  const form = useForm<CheckoutLinkFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      productId: '',
      selectionType: 'plan',
      planId: '',
      packageId: '',
      priceId: '',
      expiresAt: getDefaultExpiryInputValue(),
    },
  });

  const selectionType = form.watch('selectionType');
  const {
    data: checkoutLinkOptions,
    isLoading: optionsLoading,
    error: optionsError,
    refetch: refetchOptions,
  } = useProviderCustomerCheckoutLinkOptions(
    customerId,
    selectionType === 'plan' ? 'subscription' : undefined,
  );

  useEffect(() => {
    if (!open) {
      form.reset({
        productId: '',
        selectionType: 'plan',
        planId: '',
        packageId: '',
        priceId: '',
        expiresAt: getDefaultExpiryInputValue(),
      });
      setLatestLink(null);
      setCopied(false);
    }
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    const currentExpiry = form.getValues('expiresAt');
    if (!currentExpiry) {
      form.setValue('expiresAt', getDefaultExpiryInputValue(), { shouldValidate: true });
    }
  }, [open, form]);

  const productId = form.watch('productId');
  const planId = form.watch('planId');
  const packageId = form.watch('packageId');
  const priceId = form.watch('priceId');

  const products = checkoutLinkOptions?.products ?? [];
  const plans = checkoutLinkOptions?.plans ?? [];
  const packages = checkoutLinkOptions?.packages ?? [];
  const prices = checkoutLinkOptions?.prices ?? [];
  const cycles = checkoutLinkOptions?.cycles ?? [];

  const productMap = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);
  const planMap = useMemo(() => new Map(plans.map((plan) => [plan.id, plan])), [plans]);
  const packageMap = useMemo(() => new Map(packages.map((pkg) => [pkg.id, pkg])), [packages]);
  const priceMap = useMemo(() => new Map(prices.map((price) => [price.id, price])), [prices]);
  const cycleMap = useMemo(() => new Map(cycles.map((cycle) => [cycle.id, cycle])), [cycles]);

  const availablePlans = useMemo(
    () => plans.filter((plan) => plan.productId === productId),
    [plans, productId],
  );

  const availablePackages = useMemo(
    () => packages.filter((pkg) => pkg.productId === productId),
    [packages, productId],
  );

  const availablePrices = useMemo(() => {
    if (selectionType === 'plan') {
      if (!planId) return [];
      return prices.filter((price) => price.planId === planId);
    }
    if (!packageId) return [];
    return prices.filter((price) => price.packageId === packageId);
  }, [prices, selectionType, planId, packageId]);

  const selectedPrice = useMemo(
    () => availablePrices.find((price) => price.id === priceId) ?? null,
    [availablePrices, priceId],
  );

  const formatPriceLabel = (currency: string, amount: number | null) => {
    if (amount === null) {
      return currency;
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      console.error('Failed to format price', error);
      return `${currency} ${amount}`;
    }
  };

  useEffect(() => {
    if (!productId) {
      return;
    }
    form.setValue('planId', '');
    form.setValue('packageId', '');
    form.setValue('priceId', '');
  }, [productId, form]);

  useEffect(() => {
    form.setValue('planId', '');
    form.setValue('packageId', '');
    form.setValue('priceId', '');
  }, [selectionType, form]);

  useEffect(() => {
    if (products.length === 1 && !productId) {
      form.setValue('productId', products[0].id, { shouldValidate: true });
    }
  }, [products, productId, form]);

  useEffect(() => {
    if (selectionType === 'plan') {
      if (availablePlans.length === 1 && !planId) {
        form.setValue('planId', availablePlans[0].id, { shouldValidate: true });
      }
      return;
    }
    if (availablePackages.length === 1 && !packageId) {
      form.setValue('packageId', availablePackages[0].id, { shouldValidate: true });
    }
  }, [selectionType, availablePlans, availablePackages, planId, packageId, form]);

  useEffect(() => {
    if (availablePrices.length === 1 && !priceId) {
      form.setValue('priceId', availablePrices[0].id, { shouldValidate: true });
    }
  }, [availablePrices, priceId, form]);

  useEffect(() => {
    if (!priceId) {
      return;
    }
    const price = availablePrices.find((entry) => entry.id === priceId);
    const isValid = selectionType === 'plan'
      ? price?.planId === planId
      : price?.packageId === packageId;
    if (!isValid) {
      form.setValue('priceId', '');
    }
  }, [availablePrices, packageId, planId, priceId, selectionType, form]);

  const resolveCheckoutUrl = (
    result: ProviderCheckoutLinkCreateResult,
    input: ProviderCheckoutLinkCreateInput,
  ) => {
    if (checkoutOrigin && result.token) {
      try {
        const url = new URL('/customer/register', checkoutOrigin);
        url.searchParams.set('productId', input.productId);
        if (input.planId) {
          url.searchParams.set('planId', input.planId);
        }
        if (input.packageId) {
          url.searchParams.set('packageId', input.packageId);
        }
        if (input.cycleId) {
          url.searchParams.set('cycleId', input.cycleId);
        }
        if (input.priceId) {
          url.searchParams.set('priceId', input.priceId);
        }
        if (input.currency) {
          url.searchParams.set('currency', input.currency);
        }
        url.searchParams.set('checkout_token', result.token);
        return url.toString();
      } catch (error) {
        console.error('Failed to build checkout URL', error);
      }
    }
    if (result.checkoutUrl) {
      return result.checkoutUrl;
    }
    if (!result.token || !checkoutOrigin) {
      return '';
    }
    return '';
  };

  const handleCopyLatest = async () => {
    if (!latestLink?.checkoutUrl) {
      return;
    }

    const copyWithExecCommand = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = latestLink.checkoutUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!successful) {
          throw new Error('execCommand failed');
        }
        return true;
      } catch {
        return false;
      }
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(latestLink.checkoutUrl);
      } else if (!copyWithExecCommand()) {
        throw new Error('Clipboard API unavailable');
      }
      setCopied(true);
      toast.success(t('customers.checkoutLinks.copySuccess'));
    } catch (error) {
      console.error('Copy checkout link failed', error);
      if (copyWithExecCommand()) {
        setCopied(true);
        toast.success(t('customers.checkoutLinks.copySuccess'));
        return;
      }
      toast.error(t('customers.checkoutLinks.copyError'));
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (!customer?.id) return;
    const productValue = values.productId.trim();
    const isPlan = values.selectionType === 'plan';
    const selected = availablePrices.find((price) => price.id === values.priceId.trim());
    if (!selected) {
      toast.error(t('customers.checkoutLinks.validation.priceInvalid'));
      return;
    }
    if (isPlan && !selected.cycleId) {
      toast.error(t('customers.checkoutLinks.validation.cycleRequired'));
      return;
    }

    const expiresAtValue = values.expiresAt?.trim() || getDefaultExpiryInputValue();
    const parsedExpiry = parseDateInput(expiresAtValue);
    const normalizedExpiry = parsedExpiry ? `${expiresAtValue}T23:59:59Z` : undefined;
    const input = {
      productId: productValue,
      planId: isPlan ? values.planId?.trim() || undefined : undefined,
      packageId: !isPlan ? values.packageId?.trim() || undefined : undefined,
      cycleId: isPlan ? selected.cycleId ?? undefined : undefined,
      priceId: selected.id,
      currency: selected.currency,
      expiresAt: normalizedExpiry,
    };

    try {
      const result = await generateLink.mutateAsync({
        customerId: customer.id,
        input,
      });
      const resolvedUrl = resolveCheckoutUrl(result, input);
      const resolvedExpiresAt = result.expiresAt ?? input.expiresAt ?? null;
      setLatestLink({
        ...result,
        expiresAt: resolvedExpiresAt,
        checkoutUrl: resolvedUrl,
        productId: input.productId,
      });
      setCopied(false);
      toast.success(t('customers.checkoutLinks.generateSuccess'));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('customers.checkoutLinks.generateError');
      toast.error(message);
    }
  });

  const checkoutLinks = checkoutLinksData?.checkoutLinks ?? [];
  const sortedLinks = [...checkoutLinks].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  const renderStatusBadge = (status: string) => {
    const config = statusBadgeStyles[status] ?? statusBadgeStyles.active;
    return (
      <Badge variant={config.variant} className={config.className}>
        {t(`customers.checkoutLinks.status.${status}`, status)}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('customers.checkoutLinks.title')}</DialogTitle>
          <DialogDescription>
            {t('customers.checkoutLinks.description', { name: customer.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-lg border p-4 space-y-4">
            <div>
              <div className="text-sm font-semibold">
                {t('customers.checkoutLinks.generateTitle')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('customers.checkoutLinks.generateDescription')}
              </div>
            </div>

            {optionsError ? (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    {optionsError instanceof Error
                      ? optionsError.message
                      : t('customers.checkoutLinks.optionsError')}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => refetchOptions()}>
                    {t('customers.checkoutLinks.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}

            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="productId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.checkoutLinks.form.productId')}</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          value={field.value}
                          disabled={optionsLoading || products.length === 0}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('customers.checkoutLinks.form.productIdPlaceholder')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {products.length === 0 ? (
                              <SelectItem value="__none" disabled>
                                {t('customers.checkoutLinks.form.noProducts')}
                              </SelectItem>
                            ) : (
                              products.map((product) => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="selectionType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.checkoutLinks.form.selectionType')}</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          value={field.value}
                          disabled={optionsLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('customers.checkoutLinks.form.selectionTypePlaceholder')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="plan">
                              {t('customers.checkoutLinks.form.selectionPlan')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectionType === 'plan' ? (
                    <FormField
                      control={form.control}
                      name="planId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.checkoutLinks.form.planId')}</FormLabel>
                          <Select
                            onValueChange={(value) => field.onChange(value)}
                            value={field.value}
                            disabled={optionsLoading || !productId}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('customers.checkoutLinks.form.planIdPlaceholder')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availablePlans.length === 0 ? (
                                <SelectItem value="__none" disabled>
                                  {t('customers.checkoutLinks.form.noPlans')}
                                </SelectItem>
                              ) : (
                                availablePlans.map((plan) => (
                                  <SelectItem key={plan.id} value={plan.id}>
                                    {plan.name}
                                    {plan.isVariant ? ` (${t('customers.checkoutLinks.form.variantLabel')})` : ''}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            {t('customers.checkoutLinks.form.planHint')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : (
                    <FormField
                      control={form.control}
                      name="packageId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.checkoutLinks.form.packageId')}</FormLabel>
                          <Select
                            onValueChange={(value) => field.onChange(value)}
                            value={field.value}
                            disabled={optionsLoading || !productId}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('customers.checkoutLinks.form.packageIdPlaceholder')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availablePackages.length === 0 ? (
                                <SelectItem value="__none" disabled>
                                  {t('customers.checkoutLinks.form.noPackages')}
                                </SelectItem>
                              ) : (
                                availablePackages.map((pkg) => (
                                  <SelectItem key={pkg.id} value={pkg.id}>
                                    {pkg.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            {t('customers.checkoutLinks.form.packageHint')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="priceId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.checkoutLinks.form.priceId')}</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value)}
                          value={field.value}
                          disabled={
                            optionsLoading ||
                            !productId ||
                            (selectionType === 'plan' ? !planId : !packageId)
                          }
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('customers.checkoutLinks.form.priceIdPlaceholder')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {availablePrices.length === 0 ? (
                              <SelectItem value="__none" disabled>
                                {t('customers.checkoutLinks.form.noPrices')}
                              </SelectItem>
                            ) : (
                              availablePrices.map((price) => {
                                const cycleName = price.cycleId
                                  ? cycleMap.get(price.cycleId)?.name
                                  : null;
                                const label = formatPriceLabel(price.currency, price.amount);
                                return (
                                  <SelectItem key={price.id} value={price.id}>
                                    {cycleName ? `${label} - ${cycleName}` : label}
                                  </SelectItem>
                                );
                              })
                            )}
                          </SelectContent>
                        </Select>
                        {selectedPrice ? (
                          <FormDescription>
                            {selectedPrice.cycleId
                              ? t('customers.checkoutLinks.form.priceSummary', {
                                  currency: selectedPrice.currency,
                                  cycle: cycleMap.get(selectedPrice.cycleId)?.name ?? selectedPrice.cycleId,
                                })
                              : t('customers.checkoutLinks.form.priceSummaryNoCycle', {
                                  currency: selectedPrice.currency,
                                })}
                          </FormDescription>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="expiresAt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.checkoutLinks.form.expiresAt')}</FormLabel>
                        <FormControl>
                          <DatePickerInput
                            ariaLabel={t('customers.checkoutLinks.form.expiresAt')}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            minValue={minExpiryDate}
                            locale="en-GB"
                          />
                        </FormControl>
                        <FormDescription>
                          {t('customers.checkoutLinks.form.expiresAtHint')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <DialogFooter className="justify-end">
                  <Button
                    type="submit"
                    disabled={generateLink.isPending || optionsLoading || Boolean(optionsError)}
                  >
                    {generateLink.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('customers.checkoutLinks.generatePending')}
                      </>
                    ) : (
                      t('customers.checkoutLinks.generateAction')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>

          {latestLink ? (
            <Alert>
              <AlertDescription className="space-y-3">
                <div className="text-sm font-medium">{t('customers.checkoutLinks.latestTitle')}</div>
                <div className="break-all text-sm text-muted-foreground">
                  {latestLink.checkoutUrl || t('customers.checkoutLinks.urlUnavailable')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('customers.checkoutLinks.expiresAtLabel', { date: formatDateTime(latestLink.expiresAt) })}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyLatest} disabled={!latestLink.checkoutUrl}>
                    {copied ? <ClipboardCheck className="mr-2 h-4 w-4" /> : <Clipboard className="mr-2 h-4 w-4" />}
                    {copied ? t('customers.checkoutLinks.copyDone') : t('customers.checkoutLinks.copyAction')}
                  </Button>
                  {latestLink.checkoutUrl ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => window.open(latestLink.checkoutUrl, '_blank', 'noopener')}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t('customers.checkoutLinks.openAction')}
                    </Button>
                  ) : null}
                </div>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">{t('customers.checkoutLinks.listTitle')}</div>
                <div className="text-xs text-muted-foreground">
                  {t('customers.checkoutLinks.listDescription')}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                {t('customers.checkoutLinks.refresh')}
              </Button>
            </div>

            {isLoading ? (
              <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                {t('customers.checkoutLinks.loading')}
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    {error instanceof Error ? error.message : t('customers.checkoutLinks.loadError')}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    {t('customers.checkoutLinks.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : sortedLinks.length === 0 ? (
              <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                {t('customers.checkoutLinks.empty')}
              </div>
            ) : (
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('customers.checkoutLinks.table.status')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.product')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.planPackage')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.pricing')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.currency')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.created')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.expires')}</TableHead>
                      <TableHead>{t('customers.checkoutLinks.table.used')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell>{renderStatusBadge(link.status)}</TableCell>
                        <TableCell className="text-xs">
                          {productMap.get(link.productId)?.name ?? '--'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {link.packageId ? (
                            <>
                              <div>{t('customers.checkoutLinks.packageLabel')}</div>
                              <div className="text-muted-foreground">
                                {packageMap.get(link.packageId)?.name ?? '--'}
                              </div>
                            </>
                          ) : (
                            <>
                              <div>{t('customers.checkoutLinks.planLabel')}</div>
                              <div className="text-muted-foreground">
                                {link.planId ? planMap.get(link.planId)?.name ?? '--' : '--'}
                              </div>
                            </>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>
                            {t('customers.checkoutLinks.cycleLabel', { defaultValue: 'Cycle' })}:{' '}
                            {link.cycleId ? cycleMap.get(link.cycleId)?.name ?? '--' : '--'}
                          </div>
                          <div className="text-muted-foreground">
                            {t('customers.checkoutLinks.priceLabel', { defaultValue: 'Price' })}:{' '}
                            {link.priceId
                              ? priceMap.get(link.priceId)
                                ? formatPriceLabel(
                                    priceMap.get(link.priceId)?.currency ?? link.currency ?? 'USD',
                                    priceMap.get(link.priceId)?.amount ?? null,
                                  )
                                : link.currency ?? '--'
                              : '--'}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{link.currency || '--'}</TableCell>
                        <TableCell className="text-xs">{formatDateTime(link.createdAt)}</TableCell>
                        <TableCell className="text-xs">{formatDateTime(link.expiresAt)}</TableCell>
                        <TableCell className="text-xs">{formatDateTime(link.usedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('customers.checkoutLinks.listNote')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
